/**
 * Shared plugin registry — single source of truth for all plugin metadata.
 *
 * Consumed by both `packages/demo` and `app/mobile`.
 */
export const PLUGIN_REGISTRY = [
    {
        id: 'twitter',
        name: 'Twitter Profile',
        description: 'Prove your Twitter profile information with cryptographic verification',
        logo: '\uD835\uDD4F', // 𝕏
        resultLabel: 'Screen Name',
        accentColor: '#667eea',
        platforms: ['demo'],
        pluginConfig: {
            name: 'X Profile Prover',
            description: 'This plugin will prove your X.com profile.',
            requests: [
                {
                    method: 'GET',
                    host: 'api.x.com',
                    pathname: '/1.1/account/settings.json',
                },
            ],
            urls: ['https://x.com/*'],
        },
    },
    {
        id: 'swissbank',
        name: 'Swiss Bank',
        description: 'Verify your Swiss bank account balance securely and privately. (Login: admin / admin)',
        logo: '\uD83C\uDFE6', // 🏦
        resultLabel: 'Account Balance (CHF)',
        accentColor: '#4CAF50',
        platforms: ['demo', 'mobile'],
        pluginConfig: {
            name: 'Swiss Bank Prover',
            description: 'This plugin will prove your Swiss Bank account balance.',
            requests: [
                {
                    method: 'GET',
                    host: 'swissbank.tlsnotary.org',
                    pathname: '/balances',
                },
            ],
            urls: ['https://swissbank.tlsnotary.org/*'],
        },
    },
    {
        id: 'swissbank_hash',
        name: 'Swiss Bank (Hashed)',
        description: 'Verify your Swiss bank account balance securely and privately. (Login: admin / admin)',
        logo: '\uD83C\uDFE6', // 🏦
        resultLabel: 'Account Balance (EUR, hashed)',
        accentColor: '#4CAF50',
        platforms: ['demo', 'mobile'],
        pluginConfig: {
            name: 'Swiss Bank Prover (Hashed)',
            description: 'This plugin will prove a blinded hash of an account balance on a (dummy) Swiss bank.',
            requests: [
                {
                    method: 'GET',
                    host: 'swissbank.tlsnotary.org',
                    pathname: '/balances',
                },
            ],
            urls: ['https://swissbank.tlsnotary.org/*'],
        },
    },
    {
        id: 'spotify',
        name: 'Spotify',
        description: 'Prove your Spotify listening history and music preferences',
        logo: '\uD83C\uDFB5', // 🎵
        resultLabel: 'Top Artist',
        accentColor: '#1DB954',
        platforms: ['demo', 'mobile'],
        pluginConfig: {
            name: 'Spotify Top Artist',
            description: 'This plugin will prove your top artist on Spotify.',
            requests: [
                {
                    method: 'GET',
                    host: 'api.spotify.com',
                    pathname: '/v1/me/top/artists',
                },
            ],
            urls: ['https://developer.spotify.com/*'],
            oauthHosts: ['accounts.spotify.com', 'accounts.google.com', 'appleid.apple.com'],
        },
    },
    {
        id: 'duolingo',
        name: 'Duolingo',
        description: 'Prove your Duolingo language learning progress and achievements',
        logo: '\uD83E\uDD89', // 🦉
        resultLabel: 'Longest Streak',
        accentColor: '#58CC02',
        platforms: ['demo', 'mobile'],
        pluginConfig: {
            name: 'Duolingo Plugin',
            description: 'This plugin will prove your email and current streak on Duolingo.',
            requests: [
                {
                    method: 'GET',
                    host: 'www.duolingo.com',
                    pathname: '/2023-05-23/users/*',
                },
            ],
            urls: ['https://www.duolingo.com/*'],
        },
    },
    {
        id: 'uber',
        name: 'Uber Profile',
        description: 'Prove your Uber rider profile via GraphQL POST request',
        logo: '\uD83D\uDE97', // 🚗
        resultLabel: 'Profile',
        accentColor: '#000000',
        platforms: ['demo', 'mobile'],
        debug: true,
        pluginConfig: {
            name: 'Uber Profile Prover',
            description: 'This plugin will prove your Uber rider profile via GraphQL.',
            requests: [
                {
                    method: 'POST',
                    host: 'riders.uber.com',
                    pathname: '/graphql',
                },
            ],
            urls: ['https://riders.uber.com/*'],
        },
    },
    {
        id: 'discord_dm',
        name: 'Discord DM',
        description: 'Prove your Discord direct messages',
        logo: '\u2709\uFE0F', // ✉️
        resultLabel: 'Message',
        accentColor: '#5865F2',
        platforms: ['demo', 'mobile'],
        debug: true,
        pluginConfig: {
            name: 'Discord DM Plugin',
            description: 'This plugin will prove your Discord direct messages.',
            requests: [
                {
                    method: 'GET',
                    host: 'discord.com',
                    pathname: '/api/v9/channels/*/messages',
                },
            ],
            urls: ['https://discord.com/*'],
        },
    },
    {
        id: 'discord_profile',
        name: 'Discord Profile',
        description: 'Prove your Discord profile information',
        logo: '\uD83D\uDCAC', // 💬
        resultLabel: 'Username',
        accentColor: '#5865F2',
        platforms: ['demo', 'mobile'],
        pluginConfig: {
            name: 'Discord Profile Plugin',
            description: 'This plugin will prove your Discord username and ID.',
            requests: [
                {
                    method: 'GET',
                    host: 'discord.com',
                    pathname: '/api/v9/users/@me',
                },
            ],
            urls: ['https://discord.com/*'],
        },
    },
    {
        id: 'idme',
        name: 'ID.me Credentials',
        description: 'Prove your verified ID.me identity credentials',
        logo: '\uD83E\uDEAA', // 🪪
        resultLabel: 'Credential',
        accentColor: '#2D6A4F',
        platforms: ['demo'],
        pluginConfig: {
            name: 'ID.me Credentials',
            description: 'Prove your verified ID.me credentials.',
            requests: [
                {
                    method: 'GET',
                    host: 'account.id.me',
                    pathname: '/api/v3/credentials.json',
                },
            ],
            urls: ['https://account.id.me/*'],
        },
    },
    {
        id: 'chesscom',
        name: 'Chess.com Rating',
        description: 'Prove your current Chess.com rating',
        logo: '♟️', // ♟️
        resultLabel: 'Rating',
        accentColor: '#81b64c',
        platforms: ['demo', 'mobile'],
        debug: true,
        pluginConfig: {
            name: 'Chess.com Rating',
            description: 'Prove your current Chess.com rating.',
            requests: [
                {
                    method: 'GET',
                    host: 'www.chess.com',
                    pathname: '/callback/member/stats/*',
                },
            ],
            urls: ['https://www.chess.com/*'],
        },
    },
];
export function getPluginById(id) {
    return PLUGIN_REGISTRY.find((p) => p.id === id);
}
export function getPluginsForPlatform(platform) {
    return PLUGIN_REGISTRY.filter((p) => p.platforms.includes(platform));
}
